import { inject } from '@angular/core';
import { signalStoreFeature } from '@ngrx/signals';
import { Events, injectDispatch, withEventHandlers } from '@ngrx/signals/events';
import { tapResponse } from '@ngrx/operators';
import { exhaustMap, switchMap } from 'rxjs';
import { signingInviteEvents } from './events';
import { SigningInvitesDataService } from '../../data-access/signing-invites';
import { parseApiFailure } from '../../services/http-utils';

export function withSigningInviteEffects() {
  return signalStoreFeature(
    withEventHandlers(() => {
      const events = inject(Events);
      const service = inject(SigningInvitesDataService);
      const dispatch = injectDispatch(signingInviteEvents);

      const failure = (e: unknown) => {
        const f = parseApiFailure(e);
        return { message: f.message, code: f.code };
      };

      return {
        lookup$: events.on(signingInviteEvents.lookup).pipe(
          switchMap(({ payload: token }) =>
            service.lookup(token).pipe(
              tapResponse({
                next: (page) => dispatch.lookupSuccess(page),
                error: (e: Error) => dispatch.lookupFailed(failure(e)),
              }),
            ),
          ),
        ),
        // `exhaustMap`, deliberately: a redeem SPENDS a single-use token and accepts a contract.
        // A double-tap must be one request, not two — the second would land on a burned token and
        // read as a failure for an acceptance that actually succeeded.
        redeem$: events.on(signingInviteEvents.redeem).pipe(
          exhaustMap(({ payload }) =>
            service.redeem(payload).pipe(
              tapResponse({
                next: (accepted) => dispatch.redeemSuccess(accepted),
                error: (e: Error) => dispatch.redeemFailed(failure(e)),
              }),
            ),
          ),
        ),
      };
    }),
  );
}
